import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Plus, Pencil, ChevronDown, X, Loader2 } from 'lucide-react';
import IsoDateInput from '../../components/common/Isodateinput';
import SearchableSelect from '../../components/common/SearchableSelect';
import {
  fetchLeavePolicies,
  fetchLeaveTypesForPolicy,
  createLeavePolicy,
  updateLeavePolicy,
  setSelectedEmployeeType,
  clearError,
  selectLeavePolicies,
  selectSelectedEmployeeType,
  selectLeaveTypes,
  selectLeavePolicyLoading,
  selectLeavePolicyError,
  selectLeavePolicySaving,
  selectLeavePolicySaveError,
  selectLeaveTypesLoading,
} from '../../store/slices/leavePolicySlice';


import {
  getEmployeeCategoryList,
  selectActiveEmployeeCategories,
  selectEmployeeCategoryLoading,
} from '../../store/slices/employeeCategorySlice';

const FIELD =
  'h-9 w-full rounded-lg border border-gray-200 bg-white px-2.5 text-xs text-gray-700 outline-none focus:border-primary-500';
const LABEL = 'text-[11px] font-medium text-gray-500 mb-1 block';

const EMPTY_POLICY = {
  leaveType: '',
  leaveTypeId: '',
  annualEarn: '',
  monthlyEarn: '',
  quarter1: '',
  quarter2: '',
  quarter3: '',
  quarter4: '',
  carry: 'No',
  maxCarry: '',
  employeeTypeId: '',
  employeeTypeName: '',
  prorata: 'N',
  calculationMethod: 'ANNUAL_ALLOCATION',
  status: 'Active',
  effectiveStartDate: '',
  effectiveEndDate: '',
};

// Convert any date-ish value to YYYY-MM-DD.
const normalizeDate = (v) => {
  if (!v) return '';
  const s = String(v).trim();
  if (!s) return '';

  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;

  const usMatch = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (usMatch) {
    const [, m, d, y] = usMatch;
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }

  const parsed = new Date(s);
  if (!Number.isNaN(parsed.getTime())) {
    const y = parsed.getFullYear();
    const m = String(parsed.getMonth() + 1).padStart(2, '0');
    const d = String(parsed.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  return '';
};

export default function LeavePolicies() {
  const dispatch = useDispatch();

  // Redux state — leave policies
  const groupedPolicies = useSelector(selectLeavePolicies);
  const selectedEmployeeType = useSelector(selectSelectedEmployeeType);
  const leaveTypes = useSelector(selectLeaveTypes) || [];
  const loading = useSelector(selectLeavePolicyLoading);
  const error = useSelector(selectLeavePolicyError);
  const saving = useSelector(selectLeavePolicySaving);
  const saveError = useSelector(selectLeavePolicySaveError);
  const leaveTypesLoading = useSelector(selectLeaveTypesLoading);

  // Redux state — employee categories
  const employeeCategories = useSelector(selectActiveEmployeeCategories) || [];
  const employeeCategoriesLoading = useSelector(selectEmployeeCategoryLoading);

  // Local state for modal
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_POLICY);
  const [errors, setErrors] = useState({});

  const [initialized, setInitialized] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);

  const policies = groupedPolicies[selectedEmployeeType] || [];

  // Field setter that also clears that field's inline error
  const setField = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => (e[key] ? { ...e, [key]: undefined } : e));
  };

  // Block minus / plus / exponent characters in numeric inputs
  const blockInvalidNumberKeys = (e) => {
    if (['-', '+', 'e', 'E'].includes(e.key)) {
      e.preventDefault();
    }
  };

  // Block pasting text that contains a minus/plus/exponent
  const blockInvalidNumberPaste = (e) => {
    const pasted = e.clipboardData.getData('text');
    if (/[-+eE]/.test(pasted)) {
      e.preventDefault();
    }
  };

  // ── Load data on mount ──────────────────────────────────────────────
  useEffect(() => {
    const loadData = async () => {
      try {
        await Promise.all([
          dispatch(fetchLeavePolicies()).unwrap(),
          dispatch(fetchLeaveTypesForPolicy()).unwrap(),
          dispatch(getEmployeeCategoryList()).unwrap(),
        ]);
        setDataLoaded(true);
      } catch (error) {
        console.error('[LeavePolicies] Failed to load data:', error);
      }
    };

    loadData();
  }, [dispatch]);

  // ── Set default employee type AFTER data is loaded ──────────────────
  useEffect(() => {
    if (dataLoaded && employeeCategories.length > 0 && !initialized) {
      const executiveType = employeeCategories.find((type) => {
        const name = type.name?.toLowerCase() || '';
        return (
          name === 'executives' ||
          name === 'executive' ||
          name.includes('executive')
        );
      });

      const defaultType = executiveType || employeeCategories[0];
      dispatch(setSelectedEmployeeType(defaultType.name));
      setInitialized(true);
      dispatch(fetchLeavePolicies());
    }
  }, [dataLoaded, employeeCategories, dispatch, initialized]);

  function openAddModal() {
    setEditingId(null);
    setErrors({});
    setForm({
      ...EMPTY_POLICY,
      employeeTypeId: employeeCategories.length > 0 ? employeeCategories[0].id : '',
      employeeTypeName: employeeCategories.length > 0 ? employeeCategories[0].name : '',
    });
    setModalOpen(true);
  }

  function openEditModal(policy) {
    setEditingId(policy.id);
    setErrors({});
    setForm({
      leaveType: policy.leaveType,
      leaveTypeId: String(policy.leaveTypeId || ''),
      annualEarn: policy.annualEarn ?? '',
      monthlyEarn: policy.monthlyEarn ?? '',
      quarter1: policy.quarter1 ?? '',
      quarter2: policy.quarter2 ?? '',
      quarter3: policy.quarter3 ?? '',
      quarter4: policy.quarter4 ?? '',
      carry: policy.carry,
      maxCarry: policy.maxCarry ?? '',
      employeeTypeId: String(policy.employeeTypeId || ''),
      employeeTypeName: policy.employeeTypeName || '',
      prorata: policy.prorata || 'N',
      calculationMethod: policy.calculationMethod || 'ANNUAL_ALLOCATION',
      status: policy.status === 'A' || policy.status === 'Active' ? 'Active' : 'Inactive',
      effectiveStartDate: normalizeDate(policy.effectiveStartDate),
      effectiveEndDate: normalizeDate(policy.effectiveEndDate),
    });
    setModalOpen(true);
  }

  function closeModal() {
    setModalOpen(false);
    setEditingId(null);
    setForm(EMPTY_POLICY);
    setErrors({});
    dispatch(clearError());
  }

  const handleFilterEmployeeTypeChange = async (e) => {
    const newType = e.target.value;
    dispatch(setSelectedEmployeeType(newType));
    await dispatch(fetchLeavePolicies());
  };

  const handleFormEmployeeTypeChange = (selectedId) => {
    const selectedType = employeeCategories.find(
      (t) => String(t.id) === String(selectedId)
    );
    setField('employeeTypeId', selectedId);
    setForm((f) => ({ ...f, employeeTypeName: selectedType?.name || '' }));
  };

  async function handleSave() {
    const next = {};
    const isBlank = (v) => v === '' || v === null || v === undefined;
    const isNegative = (v) => !isBlank(v) && Number(v) < 0;

    if (!form.leaveTypeId) next.leaveTypeId = 'Please select a leave type.';
    if (!form.employeeTypeId) next.employeeTypeId = 'Please select an employee type.';

    if (isBlank(form.annualEarn)) next.annualEarn = 'Annual Earn is required.';
    else if (isNegative(form.annualEarn)) next.annualEarn = 'Must be a valid non-negative number.';

    if (isBlank(form.monthlyEarn)) next.monthlyEarn = 'Monthly Earn is required.';
    else if (isNegative(form.monthlyEarn)) next.monthlyEarn = 'Must be a valid non-negative number.';

    ['quarter1', 'quarter2', 'quarter3', 'quarter4'].forEach((key, i) => {
      if (isBlank(form[key])) next[key] = `Quarter ${i + 1} is required.`;
      else if (isNegative(form[key])) next[key] = 'Must be a valid non-negative number.';
    });

    if (form.carry === 'Yes') {
      if (isBlank(form.maxCarry))
        next.maxCarry = 'Max Carry is required when Carry Forward is Yes.';
      else if (isNegative(form.maxCarry)) next.maxCarry = 'Must be a valid non-negative number.';
    }

    if (Object.keys(next).length) {
      setErrors(next);
      return;
    }
    setErrors({});

    const cleanDate = (v) => {
      if (!v) return null;
      const s = String(v).trim();
      return s || null;
    };

    try {
      const payload = {
        empType: Number(form.employeeTypeId) || 0,
        leaveTypeId: Number(form.leaveTypeId) || 0,
        annualAllocate: Number(form.annualEarn) || 0,
        monthlyEarn: Number(form.monthlyEarn) || 0,
        quarter1: Number(form.quarter1) || 0,
        quarter2: Number(form.quarter2) || 0,
        quarter3: Number(form.quarter3) || 0,
        quarter4: Number(form.quarter4) || 0,
        prorata: form.prorata || 'N',
        carry: form.carry === 'Yes' ? 'Y' : 'N',
        maxCarry: form.carry === 'Yes' ? Number(form.maxCarry) || 0 : 0,
        isActive: form.status === 'Active' ? 'A' : 'I',
        calculationMethod: form.calculationMethod || 'ANNUAL_ALLOCATION',
        p_effectiveStartDate: cleanDate(form.effectiveStartDate),
        p_effectiveEndDate: cleanDate(form.effectiveEndDate),
      };

      if (editingId) {
        await dispatch(updateLeavePolicy({ policyId: editingId, ...payload })).unwrap();
      } else {
        await dispatch(createLeavePolicy(payload)).unwrap();
      }

      await dispatch(fetchLeavePolicies()).unwrap();
      closeModal();
    } catch (error) {
      console.error('Save failed:', error);
    }
  }

  // Format a date string for display in the table (returns '—' for empty)
  const formatDate = (v) => normalizeDate(v) || '—';

  const displayError = error || saveError;

  // ─── Table Columns ──────────────────────────────────────────────────
  const policyColumns = [
    {
      key: 'leaveType',
      label: 'Leave Type',
      width: '160px',
      render: (r) => (
        <span className="font-semibold text-gray-900">{r.leaveType || '—'}</span>
      ),
    },
    {
      key: 'annualEarn',
      label: 'Annual Earn',
      width: '110px',
      align: 'center',
      render: (r) => (
        <span className="text-gray-700 tabular-nums">{r.annualEarn ?? '—'}</span>
      ),
    },
    {
      key: 'monthlyEarn',
      label: 'Monthly Earn',
      width: '110px',
      align: 'center',
      render: (r) => (
        <span className="text-gray-700 tabular-nums">{r.monthlyEarn ?? '—'}</span>
      ),
    },
    {
      key: 'quarter1',
      label: 'Q1',
      width: '70px',
      align: 'center',
      render: (r) => (
        <span className="text-gray-700 tabular-nums">{r.quarter1 ?? 0}</span>
      ),
    },
    {
      key: 'quarter2',
      label: 'Q2',
      width: '70px',
      align: 'center',
      render: (r) => (
        <span className="text-gray-700 tabular-nums">{r.quarter2 ?? 0}</span>
      ),
    },
    {
      key: 'quarter3',
      label: 'Q3',
      width: '70px',
      align: 'center',
      render: (r) => (
        <span className="text-gray-700 tabular-nums">{r.quarter3 ?? 0}</span>
      ),
    },
    {
      key: 'quarter4',
      label: 'Q4',
      width: '70px',
      align: 'center',
      render: (r) => (
        <span className="text-gray-700 tabular-nums">{r.quarter4 ?? 0}</span>
      ),
    },
    {
      key: 'carry',
      label: 'Carry',
      width: '100px',
      align: 'center',
      render: (r) => (
        <span className="inline-flex items-center gap-1.5">
          <span
            className={`w-1.5 h-1.5 rounded-full shrink-0 ${r.carry === 'Yes' ? 'bg-emerald-500' : 'bg-gray-400'}`}
          />
          <span className="text-gray-600">{r.carry}</span>
        </span>
      ),
    },
    {
      key: 'maxCarry',
      label: 'Max Carry',
      width: '100px',
      align: 'center',
      render: (r) => (
        <span className="text-gray-700 tabular-nums">{r.maxCarry ?? '—'}</span>
      ),
    },
    {
      key: 'prorata',
      label: 'Pro Rata',
      width: '100px',
      align: 'center',
      render: (r) => {
        const isProRata = r.prorata === 'Y' || r.prorata === 'Yes';
        return (
          <span className="inline-flex items-center gap-1.5">
            <span
              className={`w-1.5 h-1.5 rounded-full shrink-0 ${isProRata ? 'bg-emerald-500' : 'bg-gray-400'}`}
            />
            <span className="text-gray-600">{isProRata ? 'Yes' : 'No'}</span>
          </span>
        );
      },
    },
    {
      key: 'effectiveStartDate',
      label: 'Effective From',
      width: '130px',
      align: 'center',
      render: (r) => (
        <span className="text-gray-600 tabular-nums whitespace-nowrap">
          {r.effectiveStartDate ? formatDate(r.effectiveStartDate) : '—'}
        </span>
      ),
    },
    {
      key: 'effectiveEndDate',
      label: 'Effective To',
      width: '130px',
      align: 'center',
      render: (r) => (
        <span className="text-gray-600 tabular-nums whitespace-nowrap">
          {r.effectiveEndDate ? formatDate(r.effectiveEndDate) : '—'}
        </span>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      width: '100px',
      align: 'center',
      render: (r) => {
        const active = r.status === 'A' || r.status === 'Active';
        return (
          <span
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${active ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'
              }`}
          >
            {active ? 'Active' : 'Inactive'}
          </span>
        );
      },
    },
    {
      key: 'actions',
      label: 'Actions',
      width: '90px',
      align: 'center',
      render: (r) => (
        <button
          onClick={() => openEditModal(r)}
          title="Edit"
          className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-gray-200 text-gray-500 hover:bg-gray-50 transition-colors"
        >
          <Pencil size={13} className="flex-shrink-0" />
        </button>
      ),
    },
  ];

  const alignClass = (align) =>
    align === 'center'
      ? 'text-center'
      : align === 'right'
        ? 'text-right'
        : 'text-left';

  return (
    <div className="p-6 space-y-4">
      {/* ── Filters ────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <span className={LABEL}>Employee Type</span>
          <div className="relative">
            <select
              value={selectedEmployeeType || ''}
              onChange={handleFilterEmployeeTypeChange}
              disabled={employeeCategoriesLoading}
              className={`${FIELD} w-48 appearance-none pr-9`}
            >
              {employeeCategories.length > 0 ? (
                employeeCategories.map((type) => (
                  <option key={type.id} value={type.name}>
                    {type.name}
                  </option>
                ))
              ) : (
                <option value="">
                  {employeeCategoriesLoading ? 'Loading...' : 'No employee types available'}
                </option>
              )}
            </select>
            <ChevronDown
              size={15}
              className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
            />
          </div>
        </div>
        <div>
          <button
            onClick={openAddModal}
            className="flex shrink-0 items-center gap-2 h-9 px-4 bg-primary-600 text-white rounded-lg text-xs font-semibold hover:bg-primary-700 transition-colors"
          >
            <Plus size={14} />
            Add New Policy
          </button>
        </div>
      </div>

      {/* ── Error Display ────────────────────────────────────────────── */}
      {displayError && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          {displayError}
        </div>
      )}

      {/* ── Table Card ───────────────────────────────────────────────── */}
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={32} className="animate-spin text-primary-600" />
          </div>
        ) : policies.length === 0 ? (
          <div className="px-5 py-10 text-center text-sm text-gray-400">
            {`No leave policies configured for ${selectedEmployeeType?.toLowerCase() || ''} employees yet.`}
          </div>
        ) : (
          <table className="w-full min-w-[600px] border-collapse text-sm">
            <thead className="sticky -top-6 z-10 bg-gray-50 shadow-[0_1px_0_0_rgb(243_244_246)]">
              <tr className="bg-gray-50">
                {policyColumns.map((col) => (
                  <th
                    key={col.key}
                    style={{ width: col.width }}
                    className={`whitespace-nowrap bg-gray-50 px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500 ${alignClass(col.align)}`}
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {policies.map((row, index) => (
                <tr
                  key={row.id ?? `row-${index}`}
                  className={index % 2 === 1 ? 'bg-surface-50/40' : ''}
                >
                  {policyColumns.map((col) => (
                    <td
                      key={col.key}
                      className={`px-5 py-4 ${alignClass(col.align)}`}
                    >
                      {col.render ? col.render(row) : (row[col.key] ?? '—')}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* ── Footer ────────────────────────────────────────────────── */}
        {!loading && policies.length > 0 && (
          <div className="flex flex-col gap-3 border-t border-gray-100 px-5 py-3 sm:flex-row sm:items-center sm:justify-between">
            <span className="text-xs text-gray-500">
              Showing <strong className="text-gray-800">{policies.length}</strong>{' '}
              {policies.length === 1 ? 'entry' : 'entries'}
            </span>
          </div>
        )}
      </div>

      {/* ── Add/Edit Modal ────────────────────────────────────────────── */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-2xl rounded-xl bg-white p-6 shadow-lg">
            <div className="mb-5 flex items-center justify-between">
              <h2 className="text-base font-semibold text-gray-900">
                {editingId ? 'Edit Leave Policy' : 'Add New Policy'}
              </h2>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>

            {saveError && (
              <div className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-700 border border-red-200">
                {saveError}
              </div>
            )}

            <div className="space-y-4">
              <ModalField label="Employee Type *" error={errors.employeeTypeId}>
                <SearchableSelect
                  value={form.employeeTypeId}
                  onChange={handleFormEmployeeTypeChange}
                  options={employeeCategories.map((type) => ({ value: type.id, label: type.name }))}
                  emptyOption={{ value: '', label: 'Select Employee Type' }}
                  placeholder="Select Employee Type"
                  loading={employeeCategoriesLoading}
                  loadingLabel="Loading employee types..."
                  searchPlaceholder="Search employee type..."
                  className={`${FIELD} w-full`}
                />
              </ModalField>

              <ModalField label="Leave Type *" error={errors.leaveTypeId}>
                <SearchableSelect
                  value={form.leaveTypeId}
                  onChange={(v) => {
                    const selected = leaveTypes.find(
                      (type) => String(type.id) === String(v)
                    );
                    setForm((f) => ({
                      ...f,
                      leaveTypeId: v,
                      leaveType: selected?.name || '',
                    }));
                    setErrors((e) => ({ ...e, leaveTypeId: undefined }));
                  }}
                  options={leaveTypes.map((type) => ({ value: type.id, label: type.name }))}
                  emptyOption={{ value: '', label: 'Select Leave Type' }}
                  placeholder="Select Leave Type"
                  loading={leaveTypesLoading}
                  loadingLabel="Loading leave types..."
                  searchPlaceholder="Search leave type..."
                  className={`${FIELD} w-full ${leaveTypesLoading ? 'bg-gray-50' : ''}`}
                />
              </ModalField>

              {/* ── Effective Dates ─────────────────────────────── */}
              <div className="grid grid-cols-2 gap-4">
                <ModalField label="Effective Start Date">
                  <IsoDateInput
                    value={form.effectiveStartDate}
                    onChange={(v) => setField('effectiveStartDate', v)}
                    max={form.effectiveEndDate || undefined}
                    className={`${FIELD} w-full block`}
                  />
                </ModalField>
                <ModalField label="Effective End Date">
                  <IsoDateInput
                    value={form.effectiveEndDate}
                    onChange={(v) => setField('effectiveEndDate', v)}
                    min={form.effectiveStartDate || undefined}
                    className={`${FIELD} w-full block`}
                  />
                </ModalField>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <ModalField label="Annual Earn *" error={errors.annualEarn}>
                  <input
                    type="number"
                    min="0"
                    value={form.annualEarn}
                    onChange={(e) => setField('annualEarn', e.target.value)}
                    onKeyDown={blockInvalidNumberKeys}
                    onPaste={blockInvalidNumberPaste}
                    placeholder="e.g. 30"
                    className={FIELD}
                  />
                </ModalField>
                <ModalField label="Monthly Earn *" error={errors.monthlyEarn}>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.monthlyEarn}
                    onChange={(e) => setField('monthlyEarn', e.target.value)}
                    onKeyDown={blockInvalidNumberKeys}
                    onPaste={blockInvalidNumberPaste}
                    placeholder="e.g. 2.5"
                    className={FIELD}
                  />
                </ModalField>
              </div>

              {/* ── Quarterly Allocations ─────────────────────────────── */}
              <div className="grid grid-cols-2 gap-4">
                <ModalField label="Quarter 1 *" error={errors.quarter1}>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.quarter1}
                    onChange={(e) => setField('quarter1', e.target.value)}
                    onKeyDown={blockInvalidNumberKeys}
                    onPaste={blockInvalidNumberPaste}
                    placeholder="e.g. 7.5"
                    className={FIELD}
                  />
                </ModalField>
                <ModalField label="Quarter 2 *" error={errors.quarter2}>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.quarter2}
                    onChange={(e) => setField('quarter2', e.target.value)}
                    onKeyDown={blockInvalidNumberKeys}
                    onPaste={blockInvalidNumberPaste}
                    placeholder="e.g. 7.5"
                    className={FIELD}
                  />
                </ModalField>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <ModalField label="Quarter 3 *" error={errors.quarter3}>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.quarter3}
                    onChange={(e) => setField('quarter3', e.target.value)}
                    onKeyDown={blockInvalidNumberKeys}
                    onPaste={blockInvalidNumberPaste}
                    placeholder="e.g. 7.5"
                    className={FIELD}
                  />
                </ModalField>
                <ModalField label="Quarter 4 *" error={errors.quarter4}>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.quarter4}
                    onChange={(e) => setField('quarter4', e.target.value)}
                    onKeyDown={blockInvalidNumberKeys}
                    onPaste={blockInvalidNumberPaste}
                    placeholder="e.g. 7.5"
                    className={FIELD}
                  />
                </ModalField>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <ModalField label="Carry Forward">
                  <SearchableSelect
                    value={form.carry}
                    onChange={(v) => {
                      setForm((f) => ({ ...f, carry: v }));
                      setErrors((e) => ({ ...e, maxCarry: undefined }));
                    }}
                    options={['No', 'Yes']}
                    searchMinOptions={6}
                    className={`${FIELD} w-full`}
                  />
                </ModalField>
                <ModalField
                  label={form.carry === 'Yes' ? 'Max Carry *' : 'Max Carry'}
                  error={errors.maxCarry}
                >
                  <input
                    type="number"
                    min="0"
                    value={form.maxCarry}
                    disabled={form.carry === 'No'}
                    onChange={(e) => setField('maxCarry', e.target.value)}
                    onKeyDown={blockInvalidNumberKeys}
                    onPaste={blockInvalidNumberPaste}
                    placeholder="e.g. 15"
                    className={`${FIELD} ${form.carry === 'No' ? 'bg-gray-50 text-gray-400' : ''}`}
                  />
                </ModalField>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <ModalField label="Pro Rata">
                  <SearchableSelect
                    value={form.prorata}
                    onChange={(v) => setField('prorata', v)}
                    options={[
                      { value: 'N', label: 'No' },
                      { value: 'Y', label: 'Yes' },
                    ]}
                    searchMinOptions={6}
                    className={`${FIELD} w-full`}
                  />
                </ModalField>
                <ModalField label="Status">
                  <SearchableSelect
                    value={form.status}
                    onChange={(v) => setField('status', v)}
                    options={['Active', 'Inactive']}
                    searchMinOptions={6}
                    className={`${FIELD} w-full`}
                  />
                </ModalField>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3 border-t border-gray-100 pt-4">
              <button
                onClick={closeModal}
                className="rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !form.leaveTypeId || !form.employeeTypeId}
                className="rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50 transition-colors"
              >
                {saving ? 'Saving...' : editingId ? 'Save Changes' : 'Add Policy'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ModalField({ label, error, children }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-sm font-medium text-gray-700">{label}</span>
      {children}
      {error && <span className="mt-1 block text-xs text-red-600">{error}</span>}
    </label>
  );
}